import {AsyncPipe} from "@angular/common";
import {Component, computed, effect, EventEmitter, input, Input, OnInit, Output, Signal, signal} from '@angular/core';
import {FlexLayoutModule} from "@angular/flex-layout";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {MatButtonModule} from "@angular/material/button";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {PageOffering} from "@core/model/side-nav.model";
import {Retailer, TenantType} from "@features/leads/retailers/retailers.model";
import {OccupancyType} from "@features/leasing/leasing.model";
import {RetailerLookupComponent} from "@shared-components/retailer-lookup/retailer-lookup.component";
import {RetailerLookupComponentOptions} from "@shared-components/retailer-lookup/retailer-lookup.model";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {BookingConfig} from "../../bookings.model";

@Component({
  selector: 'app-booking-tenant-info',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    FlexLayoutModule,
    MatIconModule,
    MatCheckboxModule,
    FormsModule,
    MatAutocompleteModule,
    RetailerLookupComponent,
    AsyncPipe,
    MatButtonToggle,
    MatButtonToggleGroup
  ],
  templateUrl: './booking-tenant-info.component.html',
  styleUrl: './booking-tenant-info.component.scss'
})
export class BookingTenantInfoComponent implements OnInit {
  @Input() offering!: PageOffering;
  @Input() bookingConfig: BookingConfig = new BookingConfig();
  retailerLookupOptions = input<Partial<RetailerLookupComponentOptions>>();

  @Output() bookingConfigChange = new EventEmitter<BookingConfig>();
  @Output() previous = new EventEmitter<void>();

  @Output() next = new EventEmitter<any>();

  retailerForm: FormGroup;
  selectedRetailer: Retailer | null = null;
  retailerId: number | null = null;

  protected allowedRetailerTenantTypes = computed<TenantType[]>(() =>
    this.retailerLookupOptions()?.allowedRetailerTenantTypes ?? Object.values(TenantType)
  );

  protected selectedRetailerTenantType = signal(
    (this.retailerLookupOptions()?.allowedRetailerTenantTypes ?? []).includes(TenantType.OFFERING) ? (this.retailerLookupOptions()?.allowedRetailerTenantTypes ?? [])[0] : TenantType.TENANT
  );

  //map original to a new computed prop so that the booking step can specify what tenant type to be used
  protected _retailerLookupOptions = computed<Partial<RetailerLookupComponentOptions>>(() => {
    return {
      ...this.retailerLookupOptions(),
      allowedRetailerTenantTypes: [this.selectedRetailerTenantType()]
    }
  });

  private readonly _TenantTypeLabels: Map<TenantType, string> = new Map([
    [TenantType.TENANT, "Single lease"],
    [TenantType.OFFERING, "Shared lease"]
  ]);

  constructor(public loader: LoadingService) {
    this.retailerForm = new FormGroup({
      firstName: new FormControl({value: null, disabled: true}, Validators.required),
      lastName: new FormControl({value: null, disabled: true}, Validators.required),
      userEmail: new FormControl({value: null, disabled: true}, [Validators.required, Validators.email]),
      retailerCompanyName: new FormControl({value: null, disabled: false}, Validators.required)
    })

    effect(() => {
      const tenantType = this.selectedRetailerTenantType();
      this.setOccupancyType(tenantType);
    });
  }

  ngOnInit() {
    this.retailerId = this.bookingConfig.retailerId;
    this.retailerForm.get('retailerCompanyName')?.setValue(this.bookingConfig.retailerId);
    this.selectedRetailerTenantType.set(this.getTenantType(this.bookingConfig.occupancyType));
  }

  selectRetailer(event: Retailer | null) {
    this.selectedRetailer = event;
    this.populateRetailerForm();
  }

  populateRetailerForm() {
    if (this.selectedRetailer != null) {
      this.retailerForm.get('userEmail')?.setValue(this.selectedRetailer.ownerEmail);
      this.retailerForm.get('firstName')?.setValue(this.selectedRetailer.ownerFirstname);
      this.retailerForm.get('lastName')?.setValue(this.selectedRetailer.ownerLastname);
    }
  }

  // FORM SUBMITTING

  submitForm() {
    this.nextStep();
  }

  resetForm() {
    this.retailerForm.reset();
  }

  get canSubmitForm() {
    return this.selectedRetailer != null && this.retailerForm.valid;
  }

  prevStep() {
    this.previous.emit();
  }

  nextStep() {
    const bookingConfig = {...this.bookingConfig};
    if (this.selectedRetailer != null) {
      bookingConfig.retailerId = this.selectedRetailer.id!;
      bookingConfig.retailerCompanyName = this.selectedRetailer.companyName;
      bookingConfig.userEmail = this.selectedRetailer.ownerEmail;
      bookingConfig.userPhoneNumber = this.selectedRetailer.phoneNumber!;

      this.bookingConfigChange.emit(bookingConfig);
      this.next.emit();
    }
  }

  getTenantType(occupancyType: OccupancyType): TenantType {
    return occupancyType == OccupancyType.SHARED ? TenantType.OFFERING : TenantType.TENANT;
  }

  setOccupancyType(tenantType: TenantType) {
    this.bookingConfig.occupancyType = tenantType == TenantType.OFFERING ? OccupancyType.SHARED : OccupancyType.SINGLE;
  }

  protected tenantTypeDisplayLabel(tenantType: TenantType): Signal<string> {
    return computed(() => this._TenantTypeLabels.get(tenantType) ?? tenantType.toString());
  }
}

